import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "./Reveal";
import { useContent } from "@/context/ContentContext";

export const CompanyProfile = () => {
  const co = useContent().company;
  const [logoOpen, setLogoOpen] = useState(false);
  const [greetingOpen, setGreetingOpen] = useState(false);
  const g = co.greeting || {};

  return (
    <section id="company" data-testid="company-section" className="bg-surface py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mb-6">{co.eyebrow}</p>
            <h2 className="font-jp text-3xl sm:text-4xl font-bold tracking-tight chrome-text leading-tight mb-10 whitespace-pre-line">
              {co.heading}
            </h2>
            <button
              type="button"
              onClick={() => setGreetingOpen(true)}
              data-testid="company-portrait-button"
              className="hud group relative block w-full overflow-hidden rounded-2xl border border-white/10 transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:glow-cyan focus:outline-none"
            >
              <span className="hud-tl" />
              <span className="hud-br" />
              <img src={co.portrait} alt="代表取締役" className="w-full h-72 object-cover object-top contrast-110 transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-5 text-left">
                <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-gold">{g.label}</p>
                <p className="font-jp text-sm text-white/90">{g.name}</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setLogoOpen(true)}
              data-testid="company-logo-button"
              className="hud group relative mt-6 flex w-full items-center gap-4 rounded-2xl border border-white/10 bg-black/40 p-5 transition-all duration-500 hover:-translate-y-1 hover:border-gold/40 hover:glow-cyan focus:outline-none"
            >
              <span className="hud-tl" />
              <span className="hud-br" />
              <img src={co.logo_image} alt="PRC Remit ロゴ" className="h-14 w-14 object-contain shrink-0" />
              <div className="text-left">
                <p className="font-display text-lg font-extrabold tracking-tight text-white">PRC<span className="text-gold"> REMIT</span></p>
                <p className="font-mono text-[11px] tracking-[0.15em] uppercase text-white/40 group-hover:text-gold transition-colors">Corporate Logo — クリックで表示</p>
              </div>
            </button>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <div className="border-t border-white/10">
            {co.info.map((r, i) => (
              <Reveal key={r.id || i} delay={i * 0.05}>
                <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-6 py-6 border-b border-white/10">
                  <dt className="sm:col-span-3 text-sm tracking-wide text-white/40">{r.label}</dt>
                  <dd className="sm:col-span-9 text-base font-light text-white/90 break-words">{r.value}</dd>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14" data-testid="company-purposes">
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-gold mb-6">// Business Purposes / 事業目的</p>
              <ol className="border-t border-white/10">
                {co.purposes.map((p, i) => (
                  <li key={p.id || i} className="flex gap-5 py-5 border-b border-white/10" data-testid={`purpose-${i + 1}`}>
                    <span className="font-display text-sm font-extrabold text-gold pt-0.5 shrink-0 w-8">{`(${i + 1})`}</span>
                    <span className="text-base font-light leading-relaxed text-white/90">{p.text}</span>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {greetingOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setGreetingOpen(false)} data-testid="greeting-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-md p-6">
            <motion.div initial={{ y: 30, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0, scale: 0.97 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()} className="hud relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl border border-gold/30 bg-surface glow-cyan">
              <span className="hud-tl" />
              <span className="hud-br" />
              <button onClick={() => setGreetingOpen(false)} data-testid="greeting-modal-close" className="absolute top-5 right-5 z-10 grid place-items-center h-9 w-9 rounded-full bg-black/50 text-white/70 hover:text-gold transition-colors">
                <X size={20} />
              </button>
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-5 mb-8">
                  <img src={co.portrait} alt="代表取締役" className="h-20 w-20 rounded-xl object-cover object-top border border-white/10" />
                  <div>
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-gold mb-1">{g.label}</p>
                    <p className="font-jp text-xl font-bold text-white">{g.name}</p>
                  </div>
                </div>
                {g.heading && (
                  <>
                    <h3 className="font-jp text-base sm:text-lg md:text-xl font-bold tracking-tight chrome-text leading-relaxed whitespace-nowrap">{g.heading}</h3>
                    <div className="hairline my-7" />
                  </>
                )}
                <div className="space-y-5 text-base font-light leading-relaxed text-white/75">
                  {(g.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
                  {(g.points || []).length > 0 && (
                    <ul className="space-y-3 py-2">
                      {g.points.map((t) => (
                        <li key={t} className="flex items-center gap-3 text-white/90">
                          <span className="h-1.5 w-1.5 rounded-full bg-gold shrink-0" />{t}
                        </li>
                      ))}
                    </ul>
                  )}
                  {(g.closing || []).map((p, i) => <p key={i}>{p}</p>)}
                </div>
                {g.signature && <p className="mt-8 text-right font-jp text-white/90">{g.signature}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {logoOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLogoOpen(false)} data-testid="logo-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 backdrop-blur-md p-6">
            <motion.div initial={{ y: 30, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0, scale: 0.96 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()} className="hud relative w-full max-w-md rounded-2xl border border-gold/30 bg-surface p-10 glow-cyan text-center">
              <span className="hud-tl" />
              <span className="hud-br" />
              <button onClick={() => setLogoOpen(false)} data-testid="logo-modal-close" className="absolute top-5 right-5 text-white/50 hover:text-gold transition-colors">
                <X size={22} />
              </button>
              <img src={co.logo_image} alt="PRC Remit ロゴマーク" className="mx-auto h-40 w-40 object-contain drop-glow" />
              <p className="mt-8 font-display text-3xl font-extrabold tracking-tight text-white">PRC<span className="text-gold"> REMIT</span></p>
              <div className="hairline my-6" />
              <p className="text-sm font-light leading-relaxed text-white/70">{co.logo_desc}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
